import React, { useEffect, useState, useRef } from 'react';
import { AlertTriangle, TrendingDown, TrendingUp, Clock, Smartphone, LayoutDashboard, BoxIcon } from 'lucide-react';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import { useLanguage } from '../../i18n/LanguageContext';
interface CardState {
  icon: BoxIcon;
  titleKey: string;
  descKey: string;
}
interface ResultData {
  before: CardState;
  after: CardState;
  stat: {
    number: string;
    labelKey: string;
  };
}
const resultsData: ResultData[] = [{
  before: {
    icon: AlertTriangle,
    titleKey: 'results.card_1_title_before',
    descKey: 'results.card_1_desc_before'
  },
  after: {
    icon: LayoutDashboard,
    titleKey: 'results.card_1_title_after',
    descKey: 'results.card_1_desc_after'
  },
  stat: {
    number: '1',
    labelKey: 'results.card_1_stat_label'
  }
}, {
  before: {
    icon: TrendingDown,
    titleKey: 'results.card_2_title_before',
    descKey: 'results.card_2_desc_before'
  },
  after: {
    icon: TrendingUp,
    titleKey: 'results.card_2_title_after',
    descKey: 'results.card_2_desc_after'
  },
  stat: {
    number: '0',
    labelKey: 'results.card_2_stat_label'
  }
}, {
  before: {
    icon: Clock,
    titleKey: 'results.card_3_title_before',
    descKey: 'results.card_3_desc_before'
  },
  after: {
    icon: Smartphone,
    titleKey: 'results.card_3_title_after',
    descKey: 'results.card_3_desc_after'
  },
  stat: {
    number: '24/7',
    labelKey: 'results.card_3_stat_label'
  }
}];
function ResultCard({
  data,
  isAfter,
  toggleCount,
  index
}: {
  data: ResultData;
  isAfter: boolean;
  toggleCount: number;
  index: number;
}) {
  const {
    t
  } = useLanguage();
  const ref = useRevealOnScroll<HTMLDivElement>(index * 150);
  const state = isAfter ? data.after : data.before;
  const Icon = state.icon;
  // Unique key per toggle so the content div remounts and re-animates
  const contentKey = `${index}-${toggleCount}`;
  return <div ref={ref} className={`reveal-visual relative rounded-2xl border overflow-hidden flex flex-col transition-all duration-500 ${isAfter ? 'bg-white border-[#EDE7F6] shadow-sm hover:shadow-lg hover:border-[#7C4DFF]/30' : 'bg-white border-red-100 hover:border-red-200'}`} data-id="element-173">
      {/* Top Accent Bar */}
      <div className={`h-1 w-full flex-shrink-0 transition-colors duration-500 ${isAfter ? 'bg-[#7C4DFF]' : 'bg-red-400'}`} data-id="element-174" />

      {/* Animated Content — remounts on every toggle via key */}
      <div key={contentKey} className="card-content-animate flex flex-col flex-1 p-7" data-id="element-175">
        {/* State Label */}
        <div className="mb-4" data-id="element-176">
          <span className={`inline-flex text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${isAfter ? 'bg-[#EDE7F6] text-[#7C4DFF]' : 'bg-red-50 text-red-500'}`} data-id="element-177">
            {isAfter ? t('results.toggle_after') : t('results.toggle_before')}
          </span>
        </div>

        {/* Icon */}
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${isAfter ? 'bg-[#EDE7F6] text-[#7C4DFF]' : 'bg-red-50 text-red-500'}`} data-id="element-178">
          <Icon size={28} strokeWidth={2} data-id="element-179" />
        </div>

        {/* Text */}
        <h3 className="text-lg font-bold text-[#1A1A2E] mb-2 leading-snug" data-id="element-180">
          {t(state.titleKey)}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed flex-grow" data-id="element-181">
          {t(state.descKey)}
        </p>

        {/* Stat — After only */}
        {isAfter ? <div className="mt-5 pt-4 border-t border-[#EDE7F6] flex items-baseline gap-2" data-id="element-182">
            <span className="text-3xl font-bold text-[#7C4DFF]" data-id="element-183">
              {data.stat.number}
            </span>
            <span className="text-xs text-gray-400 font-medium" data-id="element-184">
              {t(data.stat.labelKey)}
            </span>
          </div> /* Invisible spacer to keep card height stable */ : <div className="mt-5 pt-4 border-t border-transparent flex items-baseline gap-2 invisible" aria-hidden data-id="element-185">
            <span className="text-3xl font-bold" data-id="element-186">0</span>
            <span className="text-xs" data-id="element-187">placeholder</span>
          </div>}
      </div>
    </div>;
}
export function ResultsSection() {
  const {
    t
  } = useLanguage();
  const [isAfter, setIsAfter] = useState(true);
  const [toggleCount, setToggleCount] = useState(0);
  const headerRef = useRevealOnScroll<HTMLDivElement>(0);
  function toggle(value: boolean) {
    if (value === isAfter) return;
    setIsAfter(value);
    setToggleCount(c => c + 1);
  }
  return <section className="py-24 bg-gradient-to-b from-[#F8F5FF] to-white" id="results" data-id="element-188">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-189">
        {/* Header */}
        <div ref={headerRef} className="reveal-visual text-center mb-8" data-id="element-190">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-3" data-id="element-191">
            {t('results.title')}
          </h2>
          <p className="text-sm text-gray-400" data-id="element-192">{t('results.subtitle')}</p>
        </div>

        {/* Toggle Pill */}
        <div className="flex justify-center mb-12" data-id="element-193">
          <div className="inline-flex bg-gray-100 rounded-full p-1 shadow-inner" data-id="element-194">
            <button onClick={() => toggle(false)} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${!isAfter ? 'bg-white text-red-500 shadow-sm ring-1 ring-black/5' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-195">
              {t('results.toggle_before')}
            </button>
            <button onClick={() => toggle(true)} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${isAfter ? 'bg-[#7C4DFF] text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-196">
              {t('results.toggle_after')}
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-197">
          {resultsData.map((data, index) => <ResultCard key={index} data={data} isAfter={isAfter} toggleCount={toggleCount} index={index} data-id="element-198" />)}
        </div>
      </div>
    </section>;
}